export const REGEX = /(- .+)|(:.+)|(\(.+)|(24\/7 LIVE TELEHEALTH)|(\d{3,4}.+|)/g;
export const REMOVE = /(-)|(:)/g;

/**
 * Format health plan name
 */
export const formatHealthPlanName = (str) => {
    if (!str)
        return '';
    const name = str?.replace(REGEX, '')?.replace('FRIDAY', 'Friday');
    return name || str;
};

/**
 * Format health plan name with detail (e.g. subtitle)
 */
export const formatHealthPlanNameWithDetail = (str) => {
    if (!str)
        return '';
    const name = str?.replace(REGEX, '')?.replace('FRIDAY', 'Friday');
    const detail = str?.match(REGEX)?.join('')?.replace(REMOVE, '');
    return name ? { name, detail } : { name: str, detail: '' };
};

/**
 * Helper to grab the deductible amount
 */
export const getDeductibleAmount = (plan) => {
    if (!plan.healthPlan.deductibles || plan.healthPlan.deductibles.length === 0) {
        return null; //indicate missing deductible data
    }
    const familyDeductibles = plan.healthPlan.deductibles.filter((d) => d.familyCost === "Family");
    const individualDeductibles = plan.healthPlan.deductibles.filter((d) => d.famiylCost === "Individual");
    if (familyDeductibles.length > 0) {
        //treat as family plan
        return familyDeductibles.reduce((total, d) => total + d.amount, 0);
    }
    else if (individualDeductibles.length > 0) {
        //treat as individual plan
        return individualDeductibles.reduce((total, d) => total + d.amount, 0);
    }
    else {
        return plan.healthPlan.deductibles.reduce((total, d) => total + d.amount, 0);
    }
};

/**
 * Handles sorting plans
 */
export const sortPlans = (plans, type) => {
    switch (type) {
        case 'lowest_deductible':
            return [...plans].sort((a, b) => {
                const deductibleA = getDeductibleAmount(a);
                const deductibleB = getDeductibleAmount(b);
                //handle null cases first (e.g. Humama plans without deductible data)
                if (deductibleA === null && deductibleB === null) {
                    return 0;
                }
                else if (deductibleA === null) {
                    return 1;
                }
                else if (deductibleB === null) {
                    return -1;
                }
                else {
                    return deductibleA - deductibleB;
                }
            });
        case 'lowest_premium':
            return [...plans].sort((a, b) => {
                if (a.healthPlan.premiumWithCredit === b.healthPlan.premiumWithCredit) {
                    return a.healthPlan.individualDeductible - b.healthPlan.individualDeductible;
                }
                else {
                    return a.healthPlan.premiumWithCredit - b.healthPlan.premiumWithCredit;
                }
            });
        case 'recommended':
        default:
            return plans;
    }
};

/**
 * Standardizes the carrier name
 */
export const getCarrierName = (name: string) => {
  // CA does blues differently (split cross and shield) so they need to be first
  if (/Blue Shield of CA/i.test(name)) return 'Blue Shield';
  if (/Blue Cross of CA/i.test(name)) return 'Anthem Blue Cross';

  // then the other blues
  if (/BCBS|bcbs|Blue Cross|BlueCross|Blue Cross Blue Shield/i.test(name))
    return 'BlueCross BlueShield';

  // then the rest
  if (/Harvard Pilgrim/i.test(name)) return 'Harvard Pilgrim';
  if (/Oscar/i.test(name)) return 'Oscar';
  if (/Aetna/i.test(name)) return 'Aetna CVS Health';
  if (/Innovation/i.test(name)) return 'Innovation Health';
  if (/UHC|United/i.test(name)) return 'United Healthcare';
  if (/Quartz/i.test(name)) return 'Quartz';
  if (/PacificSource/i.test(name)) return 'PacificSource';
  if (/Kaiser/i.test(name)) return 'Kaiser Permanente';
  if (/Friday/i.test(name)) return 'Friday Health Plans';
  if (/Ambetter/i.test(name)) return 'Ambetter';
  if (/Cigna/i.test(name)) return 'Cigna';
  if (/DentaQuest/i.test(name)) return 'DentaQuest';
  if (/Molina/i.test(name)) return 'Molina';
  if (/Local Initiative Health Authority/i.test(name)) return 'L.A. Care';
  if (/New York State Catholic Health Plan/i.test(name)) return 'Fidelis Care';
  if (/MetroPlus/i.test(name)) return 'MetroPlus';
  if (/Health Insurance Plan of Greater New York/i.test(name)) return 'EmblemHealth';
  if (/Health Net of California/i.test(name)) return 'Health Net';
  if (/Sendero/i.test(name)) return 'Sendero';
  if (/Baylor Scott and White/i.test(name)) return 'Baylor Scott and White';
  return name;
};


/**
 * Determines which logo to show
 */
export const getCarrierLogo = (name: string) => {
  // CA does blues differently (split cross and shield) so they need to be first
  if (/Blue Shield of CA/i.test(name)) return 'blueca';
  if (/Anthem|Blue Cross of CA/i.test(name)) return 'anthemca';

  // then the other blues
  if (
    /BCBS|bcbs|Blue Cross|BlueCross|Blue Cross Blue Shield|Blue|HealthPlus|GHMSI|Highmark|Keystone/i.test(
      name,
    )
  )
    return 'bcbs';

  // then the rest
  if (/Harvard Pilgrim/i.test(name)) return 'harvardpilgrim';
  if (/Oscar/i.test(name)) return 'oscar';
  if (/Aetna/i.test(name)) return 'aetna';
  if (/Innovation/i.test(name)) return 'innovation';
  if (/UHC|United/i.test(name)) return 'united';
  if (/Quartz/i.test(name)) return 'quartz';
  if (/PacificSource/i.test(name)) return 'pacific';
  if (/Kaiser/i.test(name)) return 'kaiser';
  if (/Bright Health/i.test(name)) return 'bright';
  if (/Molina/i.test(name)) return 'molina';
  if (/CareSource/i.test(name)) return 'caresource';
  if (/Cigna/i.test(name)) return 'cigna';
  if (/Friday/i.test(name)) return 'friday';
  if (/Ambetter/i.test(name)) return 'ambetter';
  if (/SelectHealth/i.test(name)) return 'select';
  if (/Delta/i.test(name)) return 'delta';
  if (/EMI Health/i.test(name)) return 'emi';
  if (/Guardian/i.test(name)) return 'guardian';
  if (/Humana/i.test(name)) return 'humana';
  if (/University of Utah/i.test(name)) return 'utah';
  if (/DentaQuest|DentaTrust|DentaSpan/i.test(name)) return 'dentaquest';
  if (/Best Life/i.test(name)) return 'bestlife';
  if (/Alpha/i.test(name)) return 'alpha';
  if (/Dentegra/i.test(name)) return 'dentegra';
  if (/Dominion/i.test(name)) return 'dominion';
  if (/Health Net|Boston Medical Center Health Plan/i.test(name)) return 'healthnet';
  if (/LA Care|Local Initiative Health Authority for Los Angeles County/i.test(name))
    return 'lacare';
  if (/New York State Catholic Health Plan|Fidelis/i.test(name)) return 'fidelis';
  if (/MetroPlus/i.test(name)) return 'metroplusny';
  if (/HealthFirst/i.test(name)) return 'healthfirst';
  if (/Emblem|Health Insurance Plan of Greater New York/i.test(name)) return 'emblem';
  if (/Tufts/i.test(name)) return 'tufts';
  if (/Fallon/i.test(name)) return 'fallon';
  if (/Allways|Neighborhood Health Plan/i.test(name)) return 'allways';
  if (/Medica\s/g.test(name)) return 'medica';
  if (/UPMC/i.test(name)) return 'upmc';
  if (/Pennsylvania Health/i.test(name)) return 'pa';
  if (/Sendero/i.test(name)) return 'sendero';
  if (/Scott and White/i.test(name)) return 'scottwhite';
  if (/Moda Health/i.test(name)) return 'moda';
  if (/TRUASSURE/i.test(name)) return 'truassure';
  if (/Renaissance Dental/i.test(name)) return 'renaissance';
  if (/Priority Health/i.test(name)) return 'priority';
  if (/US Health and Life/i.test(name)) return 'ushl';
  if (/CHRISTUS/.test(name)) return 'christus';
  if (/Antidote/.test(name)) return 'antidote';
  return 'default';
};

export const getCarrierColor = (name: string) => {
  // CA does blues differently (split cross and shield) so they need to be first
  if (
    /Anthem|Blue Cross of CA|BCBS|bcbs|Blue Cross|BlueCross|Blue Cross Blue Shield|Blue|HealthPlus|GHMSI|Highmark|Keystone|Blue Shield of CA/i.test(
      name,
    )
  )
    return '#028BCC';

  if (/Harvard Pilgrim/i.test(name)) return '#E13B3E';
  if (/Oscar/i.test(name)) return '#4F50FF';
  if (/Aetna/i.test(name)) return '#7A3D94';
  if (/Inovation/i.test(name)) return '#7A3D94';
  if (/UHC|United/i.test(name)) return '#002677';
  if (/Quartz/i.test(name)) return 'quartz';
  if (/PacificSource/i.test(name)) return 'pacific';
  if (/Kaiser/i.test(name)) return '#006180';
  if (/Bright Health/i.test(name)) return '#7F4454';
  if (/Molina/i.test(name)) return '#01A0B0';
  if (/CareSource/i.test(name)) return '#84329B';
  if (/Cigna/i.test(name)) return '#0926FF';
  if (/Friday/i.test(name)) return '#842C90';
  if (/Ambetter/i.test(name)) return '#CA187D';
  if (/SelectHealth/i.test(name)) return 'select';
  if (/University of Utah/i.test(name)) return 'utah';
  if (/DentaQuest|DentaTrust|DentaSpan/i.test(name)) return 'dentaquest';
  if (/Best Life/i.test(name)) return 'bestlife';
  if (/Alpha/i.test(name)) return 'alpha';
  if (/Dentegra/i.test(name)) return 'dentegra';
  if (/Dominion/i.test(name)) return 'dominion';
  if (/Health Net|Boston Medical Center Health Plan/i.test(name)) return '#F7C506';
  if (/LA Care|Local Initiative Health Authority for Los Angeles County/i.test(name))
    return '#0096D7';
  if (/New York State Catholic Health Plan|Fidelis/i.test(name)) return '#0D8192';
  if (/MetroPlus/i.test(name)) return '#000000';
  if (/HealthFirst/i.test(name)) return '#377E3A';
  if (/Emblem|Health Insurance Plan of Greater New York/i.test(name)) return '#50247D';
  if (/Tufts/i.test(name)) return '#0567A4';
  if (/Fallon/i.test(name)) return '#006A70';
  if (/Allways|Neighborhood Health Plan/i.test(name)) return '#EA7623';
  if (/Medica\s/g.test(name)) return 'medica';
  if (/UPMC/i.test(name)) return 'upmc';
  if (/Pennsylvania Health/i.test(name)) return 'pa';
  if (/Sendero/i.test(name)) return '#57A1D6';
  if (/Scott and White/i.test(name)) return 'scottwhite';
  if (/Moda Health/i.test(name)) return 'moda';
  if (/Priority Health/i.test(name)) return '#2A6445';
  if (/US Health and Life/i.test(name)) return '#4BA591';
  if (/CHRISTUS/.test(name)) return '#602471';
  if (/Antidote/.test(name)) return '#EA7750';
  if (/default/i.test(name)) return 'transparent';
  return 'var(--c-border-light)';
};
